import React, { useState } from "react";
import { FireSimple, CaretDown, CaretUp } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

// Planned / Prescribed burns (USFS & partners via NIFC). Informational only —
// these never trigger emergency pushes and are styled purple so members don't
// confuse them with active wildfires.
export default function RxBurnsCard({ burns = [], dnrBurns = [], countyName = null, onFlyTo }) {
  const [open, setOpen] = useState(false);
  const total = burns.length + dnrBurns.length;
  if (total === 0) return null;

  const countyLc = (countyName || "").toLowerCase();
  const inCounty =
    burns.filter((b) => b.county === countyName).length +
    dnrBurns.filter((b) => (b.county || "").toLowerCase() === countyLc).length;
  const sorted = [...burns].sort((a, b) => {
    const ac = a.county === countyName ? 1 : 0;
    const bc = b.county === countyName ? 1 : 0;
    if (bc !== ac) return bc - ac;
    return (b.discovered_at || "").localeCompare(a.discovered_at || "");
  });

  return (
    <section data-testid="rx-burns-card" className="border-b border-tac-border bg-purple-500/[0.04]">
      <button
        type="button"
        data-testid="rx-burns-card-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-tac-surface/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FireSimple size={14} weight="fill" className="text-purple-400" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-text">
            Planned / Prescribed Burns
          </span>
          <span className="text-[15px] font-mono-tac tabular text-tac-faint">
            · {total}{inCounty ? ` (${inCounty} local)` : ""}
          </span>
        </div>
        {open ? (
          <CaretUp size={12} weight="bold" className="text-tac-dim" />
        ) : (
          <CaretDown size={12} weight="bold" className="text-tac-dim" />
        )}
      </button>

      {open && (
        <>
          <div className="px-4 pb-2 text-[11px] text-purple-300/90 leading-snug">
            Intentional, managed burns (USFS &amp; partners) — not emergencies. Smoke from these
            may be visible; no alert pushes are sent for them.
          </div>
          <ul className="divide-y divide-tac-border/50" data-testid="rx-burns-list">
            {sorted.map((b) => (
              <li key={b.id} data-testid={`rx-burn-row-${b.id}`} className="px-4 py-2">
                <button
                  type="button"
                  onClick={() => b.latitude != null && onFlyTo?.(b.latitude, b.longitude)}
                  className="w-full text-left group"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-display font-bold text-xs text-tac-text leading-tight group-hover:text-purple-300 transition-colors truncate">
                      {b.name}
                    </div>
                    <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-purple-500/15 text-purple-300 border-purple-500/40">
                      Planned
                    </span>
                  </div>
                  <div className="text-[15px] font-mono-tac tabular text-tac-dim flex items-center gap-2 flex-wrap">
                    {b.county && <span>{b.county} Co.</span>}
                    {b.incident_size != null && (
                      <span>{Math.round(b.incident_size).toLocaleString()} ac</span>
                    )}
                    {b.discovered_at && <span className="text-tac-faint">· since {fmtDate(b.discovered_at)}</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {dnrBurns.length > 0 && (
            <div data-testid="dnr-burns-group">
              <div className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.15em] font-bold text-purple-300/90 border-t border-tac-border/50">
                DNR Permitted · {dnrBurns.length}
              </div>
              <ul className="divide-y divide-tac-border/50" data-testid="dnr-burns-list">
                {dnrBurns.map((b) => (
                  <li key={`dnr-${b.id}`} data-testid={`dnr-burn-row-${b.id}`} className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => b.latitude != null && onFlyTo?.(b.latitude, b.longitude)}
                      className="w-full text-left group"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-display font-bold text-xs text-tac-text leading-tight group-hover:text-purple-300 transition-colors truncate">
                          {b.burn_type ? `${b.burn_type} burn` : "Permitted burn"}
                          {b.permit_number ? ` · ${b.permit_number}` : ""}
                        </div>
                        <span
                          className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                            b.status === "Approved"
                              ? "bg-purple-500/15 text-purple-300 border-purple-500/40"
                              : "bg-tac-surface text-tac-dim border-tac-border"
                          }`}
                        >
                          {b.status === "Approved" ? "Approved" : "Requested"}
                        </span>
                      </div>
                      <div className="text-[15px] font-mono-tac tabular text-tac-dim flex items-center gap-2 flex-wrap">
                        {b.county && <span className="capitalize">{b.county} Co.</span>}
                        {b.acres != null && <span>{Math.round(b.acres).toLocaleString()} ac</span>}
                        {b.planned_date && (
                          <span className="text-tac-faint">
                            · ignition {fmtDate(b.planned_date)}
                            {b.multi_day ? " (multi-day)" : ""}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <div className="px-4 py-1.5 text-[10px] font-mono-tac text-tac-faint border-t border-tac-border/40">
        Sources: NIFC WFIGS + WA DNR Burn Portal · toggle on map via Layers → Rx Burns
      </div>
    </section>
  );
}
